import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resolve image URLs - uploaded images are relative paths
export const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  // Videos and uploads need /api prefix for proper routing through ingress
  if (url.startsWith('/uploads/')) {
    return `${process.env.REACT_APP_BACKEND_URL}/api${url}`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  }
});

// Cookie helpers for PWA token persistence (more reliable than localStorage on iOS PWA)
const setTokenCookie = (token) => {
  const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `parish_token=${token}; expires=${expires}; path=/; SameSite=Lax`;
};

const getTokenCookie = () => {
  const match = document.cookie.match(/(?:^|; )parish_token=([^;]*)/);
  return match ? match[1] : null;
};

const clearTokenCookie = () => {
  document.cookie = 'parish_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax';
};

// Helper to get token from localStorage, sessionStorage, or cookie fallback
const getToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || getTokenCookie();
};

// Helper to get user from either localStorage or sessionStorage
export const getStoredUser = () => {
  const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// Helper to clear all auth data
export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  clearTokenCookie();
};

// Helper to persist token (localStorage + cookie backup)
export const persistToken = (token, user, useLocalStorage = true) => {
  if (useLocalStorage) {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
  } else {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(user));
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
  // Always set cookie as backup for PWA reliability
  setTokenCookie(token);
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error('Network error - check your connection or try again');
    }
    return Promise.reject(error);
  }
);

// Auth
export const registerUser = (data) => api.post('/auth/register', data);
export const loginUser = (data) => api.post('/auth/login', data);
export const processSession = (session_token, invite_code = null) => api.post('/auth/session', { session_token, invite_code });
export const getMe = () => api.get('/auth/me');
export const logoutUser = () => api.post('/auth/logout');
export const changePassword = (data) => api.post('/auth/change-password', data);

// User Settings
export const getUserSettings = () => api.get('/users/me/settings');
export const updateUserSettings = (data) => api.put('/users/me/settings', data);
export const deleteAccount = () => api.delete('/users/me');
export const leaveParish = (parishId) => api.post(`/parishes/${parishId}/leave`);

// Parishes
export const getParishes = () => api.get('/parishes');
export const createParish = (data) => api.post('/parishes', data);
export const getParish = (id) => api.get(`/parishes/${id}`);
export const geocodeParish = (id, address) => api.post(`/parishes/${id}/geocode`, null, { params: { address } });
export const requestMembership = (id) => api.post(`/parishes/${id}/request-membership`);
export const getMyPendingParishRequests = () => api.get('/parishes/my-pending-requests');
export const getMembershipRequests = (id) => api.get(`/parishes/${id}/requests`);
export const approveMembership = (parishId, memId) => api.post(`/parishes/${parishId}/requests/${memId}/approve`);
export const rejectMembership = (parishId, memId) => api.post(`/parishes/${parishId}/requests/${memId}/reject`);

// Posts
export const getPosts = (params = {}) => api.get('/posts', { params });
export const getParishPosts = (parishId) => api.get(`/posts/parish/${parishId}`);
export const createPost = (data) => api.post('/posts', data);
export const updatePost = (postId, data) => api.put(`/posts/${postId}`, data);
export const deletePost = (postId) => api.delete(`/posts/${postId}`);
export const likePost = (id) => api.post(`/posts/${id}/like`);
export const getPost = (id) => api.get(`/posts/${id}`);
export const getPostLikes = (id) => api.get(`/posts/${id}/likes`);
export const getComments = (id) => api.get(`/posts/${id}/comments`);
export const createComment = (id, data) => api.post(`/posts/${id}/comments`, data);
export const likeComment = (postId, commentId) => api.post(`/posts/${postId}/comments/${commentId}/like`);
export const deleteComment = (postId, commentId) => api.delete(`/posts/${postId}/comments/${commentId}`);
export const repostPost = (id, data) => api.post(`/posts/${id}/repost`, data);
export const recordPostView = (id) => api.post(`/posts/${id}/view`);
export const reportPost = (id, reason) => api.post(`/posts/${id}/report`, { reason });
export const getUserParishCount = () => api.get('/posts/user/parish-count');

// Polls
export const votePoll = (postId, optionIndices) => api.post(`/posts/${postId}/poll/vote`, { option_indices: optionIndices });
export const getPollResults = (postId) => api.get(`/posts/${postId}/poll/results`);
export const closePoll = (postId) => api.post(`/posts/${postId}/poll/close`);
export const getPollAnalytics = (postId) => api.get(`/posts/${postId}/poll/analytics`);

// Social Media Embeds (Instagram, TikTok)
export const fetchEmbed = (url) => api.post('/embeds/fetch', { url });
export const detectEmbeds = (text) => api.post('/embeds/detect', null, { params: { text } });

// Bookmark Folders
export const getBookmarkFolders = () => api.get('/bookmark-folders');
export const createBookmarkFolder = (name) => api.post('/bookmark-folders', { name });
export const updateBookmarkFolder = (folderId, name) => api.put(`/bookmark-folders/${folderId}`, { name });
export const deleteBookmarkFolder = (folderId) => api.delete(`/bookmark-folders/${folderId}`);
export const moveBookmark = (bookmarkId, folderId) => api.post('/bookmarks/move', { bookmark_id: bookmarkId, folder_id: folderId });

// Author Muting
export const muteAuthor = (authorId) => api.post(`/posts/authors/${authorId}/mute`);
export const unmuteAuthor = (authorId) => api.delete(`/posts/authors/${authorId}/mute`);
export const getMutedAuthors = () => api.get('/posts/authors/muted');

// User Blocking
export const blockUser = (userId) => api.post(`/users/block/${userId}`);
export const unblockUser = (userId) => api.delete(`/users/block/${userId}`);
export const getBlockedUsers = () => api.get('/users/blocked');

// User Following
export const followUser = (userId) => api.post(`/users/follow/${userId}`);
export const unfollowUser = (userId) => api.delete(`/users/follow/${userId}`);
export const getFollowStatus = (userId) => api.get(`/users/follow/${userId}/status`);
export const getFollowingUsers = () => api.get('/users/following/list');
export const getFollowers = () => api.get('/users/followers/list');

// Shop Following
export const followShop = (sellerId) => api.post(`/shops/${sellerId}/follow`);
export const unfollowShop = (sellerId) => api.delete(`/shops/${sellerId}/follow`);
export const getShopFollowStatus = (sellerId) => api.get(`/shops/${sellerId}/follow/status`);
export const getFollowedShops = () => api.get('/shops/following');

// Marketplace
export const getListings = (params) => api.get('/marketplace', { params });
export const createListing = (data) => api.post('/marketplace', data);
export const getListing = (id) => api.get(`/marketplace/${id}`);
export const getFeaturedListings = (parishId) => api.get(`/marketplace/featured/${parishId}`);
export const getAllFeaturedListings = () => api.get('/marketplace/featured-all');
export const toggleFeaturedListing = (listingId) => api.post(`/marketplace/${listingId}/feature`);

// Weekly Spotlight
export const getWeeklySpotlight = () => api.get('/spotlight/weekly');
export const getParishSpotlight = (parishId) => api.get(`/spotlight/parish/${parishId}`);
export const updateSpotlightSettings = (settings) => api.put('/spotlight/settings', settings);
export const getSpotlightSettings = (parishId) => api.get(`/spotlight/settings/${parishId}`);

// Messages
export const getConversations = () => api.get('/messages/conversations');
export const getMessages = (userId) => api.get(`/messages/${userId}`);
export const sendMessage = (data) => api.post('/messages', data);
export const getUnreadMessageCount = () => api.get('/messages/unread/count');
export const markConversationRead = (userId) => api.post(`/messages/conversation/${userId}/mark-read`);
export const markConversationUnread = (userId) => api.post(`/messages/conversation/${userId}/mark-unread`);

// Events
export const getEvents = (scope) => api.get('/events', { params: { scope } });
export const getParishEvents = (parishId) => api.get(`/events/parish/${parishId}`);
export const createEvent = (data) => api.post('/events', data);
export const updateEvent = (eventId, data) => api.put(`/events/${eventId}`, data);
export const deleteEvent = (eventId) => api.delete(`/events/${eventId}`);
export const rsvpEvent = (eventId, status) => api.post(`/events/${eventId}/rsvp`, null, { params: { status } });
export const getEventAttendees = (eventId) => api.get(`/events/${eventId}/attendees`);
export const getMyRsvps = () => api.get('/events/my/rsvps');
export const getMyCreatedEvents = () => api.get('/events/my/created');
export const updateRsvpReminder = (rsvpId, reminderTimes, smsEnabled = false) => api.put(`/events/rsvp/${rsvpId}/reminder`, null, { params: { reminder_times: reminderTimes, sms_enabled: smsEnabled } });
export const cancelRsvp = (rsvpId) => api.delete(`/events/rsvp/${rsvpId}`);

// Recurring Events
export const getRecurringEvents = (parishId) => api.get(`/events/recurring/parish/${parishId}`);
export const createRecurringEvent = (data) => api.post('/events/recurring', data);
export const updateRecurringEvent = (eventId, data) => api.put(`/events/recurring/${eventId}`, data);
export const deleteRecurringEvent = (eventId) => api.delete(`/events/recurring/${eventId}`);

// Prayer Requests (Legacy - kept for backward compatibility)
export const getPrayerRequests = (scope) => api.get('/prayer-requests', { params: { scope } });
export const createPrayerRequest = (data) => api.post('/prayer-requests', data);
export const togglePray = (prayerId) => api.post(`/prayer-requests/${prayerId}/pray`);
export const deletePrayerRequest = (prayerId) => api.delete(`/prayer-requests/${prayerId}`);

// Community Requests (new unified system)
export const getRequestTypes = () => api.get('/requests/types');
export const getRequests = (params) => api.get('/requests', { params });
export const getUserRequests = (userId) => api.get(`/requests/user/${userId}`);
export const createRequest = (data) => api.post('/requests', data);
export const getRequest = (requestId) => api.get(`/requests/${requestId}`);
export const updateRequest = (requestId, data) => api.put(`/requests/${requestId}`, data);
export const togglePrayRequest = (requestId) => api.post(`/requests/${requestId}/pray`);
export const toggleOfferHelp = (requestId) => api.post(`/requests/${requestId}/offer-help`);
export const deleteRequest = (requestId) => api.delete(`/requests/${requestId}`);

// Reports
export const reportContent = (data) => api.post('/reports', data);
export const getReports = () => api.get('/reports');
export const resolveReport = (reportId, action) => api.post(`/reports/${reportId}/resolve`, null, { params: { action } });

// Bookmarks
export const getBookmarks = () => api.get('/bookmarks');
export const toggleBookmark = (data) => api.post('/bookmarks', data);
export const deleteBookmark = (bookmarkId) => api.delete(`/bookmarks/${bookmarkId}`);
export const checkBookmarks = (postIds, listingIds) => api.get('/bookmarks/check', { params: { post_ids: postIds, listing_ids: listingIds } });

// AI
export const moderateContent = (content) => api.post('/ai/moderate', { content });
export const generateDescription = (data) => api.post('/ai/generate-description', data);

// Profile
export const getUserProfile = (id) => api.get(`/users/${id}`);
export const updateProfile = (data) => api.put('/users/me', data);
export const getReligiousRoles = () => api.get('/users/religious-roles/list');
export const getRelationshipStatuses = () => api.get('/users/relationship-statuses');
export const getProfileCompleteness = () => api.get('/users/me/profile-completeness');
export const getUserMedia = (userId, limit = 50, offset = 0) => 
  api.get(`/users/${userId}/media`, { params: { limit, offset } });

// Saved Location for Marketplace
export const getSavedLocation = () => api.get('/users/me/location');
export const saveLocation = (data) => api.put('/users/me/location', data);
export const clearSavedLocation = () => api.delete('/users/me/location');

// Upload
export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const uploadFile = (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress ? (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress(percentCompleted);
    } : undefined,
    timeout: 120000 // 2 minute timeout for video uploads
  });
};

export const uploadMultipleFiles = (files, onProgress) => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  return api.post('/upload/multiple', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress ? (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress(percentCompleted);
    } : undefined,
    timeout: 120000
  });
};

// Video Processing Status (local)
export const getVideoStatus = (videoId) => api.get(`/upload/video/${videoId}/status`);

// Cloudflare Stream Video
// max_duration_seconds: estimated video duration in seconds (affects quota until processing completes)
export const createStreamUpload = (maxDurationSeconds = 600) => 
  api.post('/stream/upload/direct', { max_duration_seconds: maxDurationSeconds }, { timeout: 60000 });
export const createTusUpload = (fileSize, fileName, maxDurationSeconds = 7200) => 
  api.post('/stream/upload/tus', { file_size: fileSize, file_name: fileName, max_duration_seconds: maxDurationSeconds }, { timeout: 60000 });
// Proxy upload for large files (> 200MB) - server handles TUS
export const proxyUploadVideo = (file, maxDurationSeconds, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('max_duration_seconds', maxDurationSeconds.toString());
  
  return api.post('/stream/upload/proxy', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60 * 60 * 1000, // 1 hour timeout
    onUploadProgress: onProgress
  });
};

// Mobile-friendly chunked upload (sends small chunks one at a time)
export const initChunkedUpload = (fileName, fileSize, maxDurationSeconds = 600) =>
  api.post('/stream/upload/chunked/init', { file_name: fileName, file_size: fileSize, max_duration_seconds: maxDurationSeconds }, { timeout: 60000 });
export const uploadChunk = (uploadId, chunkIndex, chunkBlob) => {
  const formData = new FormData();
  formData.append('file', chunkBlob, `chunk_${chunkIndex}`);
  return api.post(`/stream/upload/chunked/${uploadId}/chunk/${chunkIndex}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000 // 1 min per chunk
  });
};
export const completeChunkedUpload = (uploadId) =>
  api.post(`/stream/upload/chunked/${uploadId}/complete`, {}, { timeout: 10 * 60 * 1000 }); // 10 min for assembly
export const getChunkedUploadStatus = (uploadId) =>
  api.get(`/stream/upload/chunked/${uploadId}/status`);

export const getStreamVideoStatus = (videoId) => api.get(`/stream/video/${videoId}/status`);
export const deleteStreamVideo = (videoId) => api.delete(`/stream/video/${videoId}`);
export const listStreamVideos = (limit = 20, offset = 0) => 
  api.get('/stream/videos', { params: { limit, offset } });
export const checkStreamHealth = () => api.get('/stream/health');
// Admin: Cloudflare Storage Management
export const listAllCloudflareVideos = () => api.get('/stream/admin/all-videos');
export const cleanupPendingVideos = () => api.delete('/stream/admin/cleanup-pending');
export const adminDeleteVideo = (videoId) => api.delete(`/stream/admin/video/${videoId}`);

// Admin - Site Admin Only
export const getAdminStats = () => api.get('/admin/stats');
export const getReportedPosts = (status = 'pending') => api.get('/admin/reports', { params: { status } });
export const takeReportAction = (reportId, action, note) => api.post(`/admin/reports/${reportId}/action`, { action, note });
export const getReportHistory = () => api.get('/admin/reports/history');
export const getAdminUsers = (params) => api.get('/admin/users', { params });
export const updateUserRole = (userId, role, isSiteAdmin) => api.put(`/admin/users/${userId}/role`, { role, is_site_admin: isSiteAdmin });
export const getUserWarnings = (userId) => api.get(`/admin/users/${userId}/warnings`);

// Admin - User Management
export const getUserDetails = (userId) => api.get(`/admin/users/${userId}/details`);
export const banUser = (userId, reason, durationDays) => api.post(`/admin/users/${userId}/ban`, { reason, duration_days: durationDays });
export const unbanUser = (userId, note) => api.post(`/admin/users/${userId}/unban`, { note });
export const getBannedUsers = () => api.get('/admin/users/banned');
export const deleteUser = (userId, reason) => api.delete(`/admin/users/${userId}`, { params: { reason } });
export const forcePasswordReset = (userId) => api.post(`/admin/users/${userId}/force-password-reset`);
export const promoteSiteAdmin = (userId) => api.post(`/admin/users/${userId}/promote-admin`);
export const demoteSiteAdmin = (userId) => api.post(`/admin/users/${userId}/demote-admin`);

// Admin - Parish Management
export const getAdminParishes = (params) => api.get('/admin/parishes', { params });
export const createAdminParish = (data) => api.post('/admin/parishes', data);
export const getAdminParishDetails = (parishId) => api.get(`/admin/parishes/${parishId}`);
export const updateAdminParish = (parishId, data) => api.put(`/admin/parishes/${parishId}`, data);
export const deleteAdminParish = (parishId) => api.delete(`/admin/parishes/${parishId}`);
export const suspendParish = (parishId) => api.post(`/admin/parishes/${parishId}/suspend`);
export const reactivateParish = (parishId) => api.post(`/admin/parishes/${parishId}/reactivate`);
export const adminAssignParishAdmin = (parishId, userId) => api.post(`/admin/parishes/${parishId}/admins/${userId}`);
export const adminRemoveParishAdmin = (parishId, userId) => api.delete(`/admin/parishes/${parishId}/admins/${userId}`);
export const assignUserToParish = (userId, parishId, makeAdmin = false) => api.post(`/admin/users/${userId}/assign-parish`, { parish_id: parishId, make_admin: makeAdmin });
export const getParishesListForAssignment = () => api.get('/admin/parishes-list');

// Admin - Analytics
export const getAnalyticsOverview = () => api.get('/admin/analytics/overview');
export const getUserGrowth = (days = 30) => api.get('/admin/analytics/user-growth', { params: { days } });
export const getParishRankings = (limit = 10) => api.get('/admin/analytics/parish-rankings', { params: { limit } });
export const getContentStats = () => api.get('/admin/analytics/content-stats');

// Admin - Announcements
export const getSiteAnnouncements = () => api.get('/admin/announcements');
export const createSiteAnnouncement = (data) => api.post('/admin/announcements', data);
export const updateSiteAnnouncement = (id, data) => api.put(`/admin/announcements/${id}`, data);
export const deleteSiteAnnouncement = (id) => api.delete(`/admin/announcements/${id}`);
export const toggleAnnouncement = (id) => api.post(`/admin/announcements/${id}/toggle`);
export const getActiveAnnouncements = () => api.get('/admin/announcements/active');

// Admin - Content Management
export const getAdminPosts = (params) => api.get('/admin/content/posts', { params });
export const deletePostAdmin = (postId) => api.delete(`/admin/content/posts/${postId}`);
export const getAdminListings = (params) => api.get('/admin/content/listings', { params });
export const deleteListingAdmin = (listingId) => api.delete(`/admin/content/listings/${listingId}`);
export const getAdminGroups = (params) => api.get('/admin/content/groups', { params });
export const deleteGroupAdmin = (groupId) => api.delete(`/admin/content/groups/${groupId}`);

// Admin - Business Directory Management
export const getAdminBusinesses = (params) => api.get('/admin/content/businesses', { params });
export const suspendBusinessAdmin = (listingId) => api.post(`/admin/content/businesses/${listingId}/suspend`);
export const reactivateBusinessAdmin = (listingId) => api.post(`/admin/content/businesses/${listingId}/reactivate`);
export const deleteBusinessAdmin = (listingId) => api.delete(`/admin/content/businesses/${listingId}`);

// Admin - Requests Management (Prayer Requests, Donations, etc.)
export const getAdminRequests = (params) => api.get('/admin/requests', { params });
export const suspendRequestAdmin = (requestId) => api.put(`/admin/requests/${requestId}/suspend`);
export const reactivateRequestAdmin = (requestId) => api.put(`/admin/requests/${requestId}/reactivate`);
export const deleteRequestAdmin = (requestId) => api.delete(`/admin/requests/${requestId}`);

// Admin - Parish Join Requests (consolidated view for site admins)
export const getAdminJoinRequests = (status = 'pending') => api.get('/admin/parish-join-requests', { params: { status } });
export const adminApproveJoinRequest = (membershipId) => api.post(`/admin/parish-join-requests/${membershipId}/approve`);
export const adminRejectJoinRequest = (membershipId) => api.post(`/admin/parish-join-requests/${membershipId}/reject`);

// Admin - System Settings
export const getSystemSettings = () => api.get('/admin/settings');
export const updateSystemSettings = (data) => api.put('/admin/settings', data);
export const getAdminLogs = (params) => api.get('/admin/logs', { params });

// Notifications
export const getNotifications = () => api.get('/notifications');
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markAllNotificationsRead = () => api.post('/notifications/mark-read');
export const markNotificationRead = (notificationId) => api.post(`/notifications/${notificationId}/read`);

// User Search
export const searchUsers = (q, params = {}) => api.get('/users/search', { params: { q, ...params } });

// Seed
export const seedData = () => api.post('/seed');

// Push Notifications
export const getVapidPublicKey = () => api.get('/push/vapid-public-key');
export const subscribePush = (subscription) => api.post('/push/subscribe', subscription);
export const unsubscribePush = (subscription) => api.post('/push/unsubscribe', subscription);
export const getPushStatus = () => api.get('/push/status');
export const updateNotificationPreferences = (preferences) => api.put('/push/preferences', preferences);

// Stripe Payments
export const createCheckoutSession = (listingId) => api.post('/checkout/create-session', {
  listing_id: listingId,
  origin_url: window.location.origin
});
export const getCheckoutStatus = (sessionId) => api.get(`/checkout/status/${sessionId}`);
export const getUserPurchases = () => api.get('/purchases');
export const getUserSales = () => api.get('/sales');

// Order Management
export const getOrder = (orderId) => api.get(`/orders/${orderId}`);
export const updateOrderStatus = (orderId, data) => api.put(`/orders/${orderId}/status`, data);
export const getOrderMessages = (orderId) => api.get(`/orders/${orderId}/messages`);
export const addOrderMessage = (orderId, content) => api.post(`/orders/${orderId}/message`, null, { params: { content } });

// Reviews
export const createReview = (orderId, data) => api.post(`/orders/${orderId}/review`, data);
export const getOrderReview = (orderId) => api.get(`/orders/${orderId}/review`);
export const getUserReviews = (userId) => api.get(`/users/${userId}/reviews`);

// Activity Feed
export const getUserActivity = (userId) => api.get(`/users/${userId}/activity`);
export const updateActivityPrivacy = (activityPublic) => api.put('/users/me/activity-privacy', { activity_public: activityPublic });
export const getActivityPrivacy = () => api.get('/users/me/activity-privacy');

// Advanced Marketplace
export const getListingsV2 = (params) => api.get('/marketplace/v2', { params });
export const createListingV2 = (data) => api.post('/marketplace/v2', data);
export const updateListing = (listingId, data) => api.put(`/marketplace/${listingId}`, data);
export const deleteListing = (listingId) => api.delete(`/marketplace/${listingId}`);
export const updateListingImages = (listingId, imageUrls) => api.put(`/marketplace/${listingId}/images`, imageUrls);
export const getListingDetail = (listingId) => api.get(`/marketplace/${listingId}`);
export const getListingById = (listingId) => api.get(`/marketplace/${listingId}`);
export const getListingReviews = (listingId) => api.get(`/marketplace/${listingId}/reviews`);
export const createListingReview = (listingId, data) => api.post(`/marketplace/${listingId}/reviews`, data);
export const getSellerReviews = (sellerId) => api.get(`/marketplace/seller/${sellerId}/reviews`);
export const getParishesList = () => api.get('/parishes/list');

// Seller Dashboard
export const getSellerDashboard = () => api.get('/seller/dashboard');
export const getSellerEarnings = () => api.get('/seller/earnings');

// Shopping Cart
export const getCart = () => api.get('/cart');
export const addToCart = (listingId, quantity = 1) => api.post('/cart/add', { listing_id: listingId, quantity });
export const updateCartItem = (listingId, quantity) => api.put(`/cart/item/${listingId}`, { quantity });
export const removeFromCart = (listingId) => api.delete(`/cart/item/${listingId}`);
export const clearCart = () => api.delete('/cart');
export const checkoutCart = (originUrl) => api.post('/cart/checkout', { origin_url: originUrl });
export const getCartCount = () => api.get('/cart/count');

// Sacrament Finder
export const getSacramentOptions = () => api.get('/sacraments/options');
export const findSacraments = (filters) => api.post('/sacraments/find', filters);
export const getParishSacraments = (parishId) => api.get(`/sacraments/parish/${parishId}`);
export const geocodeAddress = (address) => api.post('/sacraments/geocode', null, { params: { address } });

// Parish Admin Management
export const updateParish = (parishId, data) => api.put(`/parishes/${parishId}`, data);
export const getParishAdmins = (parishId) => api.get(`/parishes/${parishId}/admins`);
export const assignParishAdmin = (parishId, userId) => api.post(`/parishes/${parishId}/admins`, { user_id: userId });
export const removeParishAdmin = (parishId, userId) => api.delete(`/parishes/${parishId}/admins/${userId}`);
export const getParishMembers = (parishId) => api.get(`/parishes/${parishId}/members`);

// Parish Admin - Member Management
export const suspendParishMember = (parishId, userId, reason, durationDays) => 
  api.post(`/parishes/${parishId}/members/${userId}/suspend`, { reason, duration_days: durationDays });
export const reinstateParishMember = (parishId, userId) => 
  api.post(`/parishes/${parishId}/members/${userId}/reinstate`);
export const removeParishMember = (parishId, userId, reason) => 
  api.delete(`/parishes/${parishId}/members/${userId}`, { params: { reason } });
export const getSuspendedParishMembers = (parishId) => 
  api.get(`/parishes/${parishId}/members/suspended`);

// Parish Invite Links
export const generateInviteLink = (parishId) => api.post(`/parishes/${parishId}/invite`);
export const getParishInvite = (parishId) => api.get(`/parishes/${parishId}/invite`);
export const revokeInviteLink = (parishId, inviteCode) => api.delete(`/parishes/${parishId}/invite/${inviteCode}`);
export const getInviteInfo = (inviteCode) => api.get(`/parishes/invite/${inviteCode}/info`);
export const acceptInvite = (inviteCode) => api.post(`/parishes/invite/${inviteCode}/accept`);

// Dioceses (for Ordo Amoris)
export const getDioceses = () => api.get('/parishes/dioceses');
export const getParishesByDiocese = (diocese) => api.get('/parishes/by-diocese', { params: { diocese } });

// Admin directory management
export const adminCreateListing = (data) => api.post('/directory/admin/create', data);
export const adminUpdateListing = (listingId, data) => api.put(`/directory/admin/${listingId}`, data);
export const adminAssignOwner = (listingId, userId) => api.post(`/directory/admin/${listingId}/assign-owner?user_id=${userId}`);

// Languages (for Ordo Amoris)
export const getAvailableLanguages = () => api.get('/users/languages');

// Friends
export const getFriends = () => api.get('/friends');
export const getUserFriends = (userId) => api.get(`/friends/user/${userId}`);
export const getFriendRequests = () => api.get('/friends/requests');
export const getFriendshipStatus = (userId) => api.get(`/friends/status/${userId}`);
export const sendFriendRequest = (userId) => api.post('/friends/request', { user_id: userId });
export const acceptFriendRequest = (friendshipId) => api.post(`/friends/accept/${friendshipId}`);
export const rejectFriendRequest = (friendshipId) => api.post(`/friends/reject/${friendshipId}`);
export const cancelFriendRequest = (friendshipId) => api.delete(`/friends/cancel/${friendshipId}`);
export const unfriend = (userId) => api.delete(`/friends/${userId}`);

// Friend Suggestions
export const getFriendSuggestions = (limit = 10) => api.get('/users/suggestions', { params: { limit } });
export const dismissFriendSuggestion = (userId) => api.post(`/users/suggestions/${userId}/dismiss`);

// Family (for Ordo Amoris)
export const getFamilyRelationshipTypes = () => api.get('/family/types');
export const getFamilyMembers = () => api.get('/family');
export const getUserFamily = (userId) => api.get(`/family/user/${userId}`);
export const getFamilyRequests = () => api.get('/family/requests');
export const getFamilyStatus = (userId) => api.get(`/family/status/${userId}`);
export const sendFamilyRequest = (userId, relationshipType) => api.post('/family/request', { user_id: userId, relationship_type: relationshipType });
export const acceptFamilyRequest = (relationshipId) => api.post(`/family/accept/${relationshipId}`);
export const rejectFamilyRequest = (relationshipId) => api.post(`/family/reject/${relationshipId}`);
export const cancelFamilyRequest = (relationshipId) => api.delete(`/family/cancel/${relationshipId}`);
export const removeFamilyMember = (userId) => api.delete(`/family/${userId}`);

// Work History
export const getMyWorkHistory = () => api.get('/users/me/work');
export const addWorkEntry = (data) => api.post('/users/me/work', data);
export const updateWorkEntry = (entryId, data) => api.put(`/users/me/work/${entryId}`, data);
export const deleteWorkEntry = (entryId) => api.delete(`/users/me/work/${entryId}`);

// Education History
export const getMyEducationHistory = () => api.get('/users/me/education');
export const addEducationEntry = (data) => api.post('/users/me/education', data);
export const updateEducationEntry = (entryId, data) => api.put(`/users/me/education/${entryId}`, data);
export const deleteEducationEntry = (entryId) => api.delete(`/users/me/education/${entryId}`);

// Business Directory
export const getBusinessCategories = () => api.get('/directory/categories');
export const getBusinessListings = (params) => api.get('/directory', { params });
export const getMyBusinessListings = () => api.get('/directory/my-listings');
export const getBusinessListing = (listingId, userLat, userLng) => 
  api.get(`/directory/${listingId}`, { params: { user_lat: userLat, user_lng: userLng } });
export const createBusinessListing = (data) => api.post('/directory', data);
export const updateBusinessListing = (listingId, data) => api.put(`/directory/${listingId}`, data);
export const deleteBusinessListing = (listingId) => api.delete(`/directory/${listingId}`);
export const addBusinessReview = (listingId, data) => api.post(`/directory/${listingId}/review`, data);
export const updateBusinessReview = (listingId, reviewId, data) => api.put(`/directory/${listingId}/review/${reviewId}`, data);
export const deleteBusinessReview = (listingId, reviewId) => api.delete(`/directory/${listingId}/review/${reviewId}`);
export const getRatingBreakdown = (listingId) => api.get(`/directory/${listingId}/rating-breakdown`);
export const toggleReviewHelpful = (listingId, reviewId) => api.post(`/directory/${listingId}/review/${reviewId}/helpful`);
export const toggleBusinessRecommendation = (listingId) => api.post(`/directory/${listingId}/recommend`);
export const checkBusinessRecommendation = (listingId) => api.get(`/directory/${listingId}/recommended`);

// Business Directory - Map & Featured
export const getBusinessMapListings = (params) => api.get('/directory/map/listings', { params });
export const toggleBusinessFeatured = (listingId, featured, featuredUntil) => 
  api.post(`/directory/${listingId}/feature`, { featured, featured_until: featuredUntil });
export const getFeaturedBusinessListings = () => api.get('/directory/admin/featured');

// Featured Listings (Sponsored Ads)
export const getFeaturedTiers = () => api.get('/featured/tiers');
export const createFeaturedCheckout = (data) => api.post('/featured/checkout', data);
export const getFeaturedCheckoutStatus = (sessionId) => api.get(`/featured/checkout/status/${sessionId}`);
export const getListingFeaturedStatus = (listingId) => api.get(`/featured/listing/${listingId}/status`);
export const getActiveFeaturedListings = (params) => api.get('/featured/active', { params });
export const getMyFeaturedListings = () => api.get('/featured/my-featured');

// Groups
export const getGroupCategories = () => api.get('/groups/categories');
export const getGroups = (params) => api.get('/groups', { params });
export const getGroup = (groupId) => api.get(`/groups/${groupId}`);
export const createGroup = (data) => api.post('/groups', data);
export const updateGroup = (groupId, data) => api.put(`/groups/${groupId}`, data);
export const deleteGroup = (groupId) => api.delete(`/groups/${groupId}`);
export const joinGroup = (groupId) => api.post(`/groups/${groupId}/join`);
export const leaveGroup = (groupId) => api.post(`/groups/${groupId}/leave`);
export const getGroupRequests = (groupId) => api.get(`/groups/${groupId}/requests`);
export const approveGroupRequest = (groupId, requestId) => api.post(`/groups/${groupId}/requests/${requestId}/approve`);
export const rejectGroupRequest = (groupId, requestId) => api.post(`/groups/${groupId}/requests/${requestId}/reject`);
export const removeGroupMember = (groupId, memberId) => api.post(`/groups/${groupId}/members/${memberId}/remove`);
export const addGroupAdmin = (groupId, memberId) => api.post(`/groups/${groupId}/admins/${memberId}`);
export const removeGroupAdmin = (groupId, memberId) => api.delete(`/groups/${groupId}/admins/${memberId}`);
export const transferGroupOwnership = (groupId, newOwnerId) => api.post(`/groups/${groupId}/transfer-ownership/${newOwnerId}`);
export const giveUpGroupAdmin = (groupId) => api.post(`/groups/${groupId}/give-up-admin`);

// Group Posts (Feed)
export const getGroupPosts = (groupId) => api.get(`/groups/${groupId}/posts`);
export const createGroupPost = (groupId, data) => api.post(`/groups/${groupId}/posts`, data);
export const likeGroupPost = (groupId, postId) => api.post(`/groups/${groupId}/posts/${postId}/like`);
export const deleteGroupPost = (groupId, postId) => api.delete(`/groups/${groupId}/posts/${postId}`);
export const getGroupPostComments = (groupId, postId) => api.get(`/groups/${groupId}/posts/${postId}/comments`);
export const createGroupPostComment = (groupId, postId, data) => api.post(`/groups/${groupId}/posts/${postId}/comments`, data);

// Group Events
export const getGroupEvents = (groupId) => api.get(`/groups/${groupId}/events`);
export const createGroupEvent = (groupId, data) => api.post(`/groups/${groupId}/events`, data);
export const updateGroupEvent = (groupId, eventId, data) => api.put(`/groups/${groupId}/events/${eventId}`, data);
export const deleteGroupEvent = (groupId, eventId) => api.delete(`/groups/${groupId}/events/${eventId}`);
export const toggleGroupEventRsvp = (groupId, eventId) => api.post(`/groups/${groupId}/events/${eventId}/rsvp`);
export const getGroupEventAttendees = (groupId, eventId) => api.get(`/groups/${groupId}/events/${eventId}/attendees`);

// Group Chat
export const getGroupChat = (groupId, before) => api.get(`/groups/${groupId}/chat`, { params: { before } });
export const sendGroupChatMessage = (groupId, content) => api.post(`/groups/${groupId}/chat`, { content });
export const deleteGroupChatMessage = (groupId, messageId) => api.delete(`/groups/${groupId}/chat/${messageId}`);
export const getNewGroupChatMessages = (groupId, after) => api.get(`/groups/${groupId}/chat/new`, { params: { after } });

// Group Invitations
export const inviteToGroup = (groupId, userIds, message) => api.post(`/groups/${groupId}/invite`, { user_ids: userIds, message });
export const getReceivedGroupInvitations = () => api.get('/groups/invitations/received');
export const acceptGroupInvitation = (inviteId) => api.post(`/groups/invitations/${inviteId}/accept`);
export const declineGroupInvitation = (inviteId) => api.post(`/groups/invitations/${inviteId}/decline`);

// Invites
export const getMyParishesForInvite = () => api.get('/invites/my-parishes');
export const createInvite = (data) => api.post('/invites/create', data);
export const getMyInvites = () => api.get('/invites/my-invites');
export const validateInvite = (inviteCode) => api.get(`/invites/validate/${inviteCode}`);
export const revokeInvite = (inviteCode) => api.delete(`/invites/${inviteCode}`);
export const getAdminInvites = (page = 1) => api.get('/invites/admin/all', { params: { page } });
export const getInviteStats = () => api.get('/invites/admin/stats');

// Support Tickets
export const createSupportTicket = (data) => api.post('/support/tickets', data);
export const submitClaimListing = (data) => axios.post(`${API_BASE}/support/claim-listing`, data);
export const getMySupportTickets = () => api.get('/support/tickets/my');
export const getAdminSupportTickets = (status, page = 1) => api.get('/support/admin/tickets', { params: { status, page } });
export const getSupportTicketStats = () => api.get('/support/admin/tickets/stats');
export const updateSupportTicket = (ticketId, data) => api.put(`/support/admin/tickets/${ticketId}`, data);
export const deleteSupportTicket = (ticketId) => api.delete(`/support/admin/tickets/${ticketId}`);


// Parish Staff Search
export const searchParishMembers = (parishId, query) => api.get(`/parishes/${parishId}/members/search`, { params: { query } });

// Shops
export const createShop = (data) => api.post('/shops', data);
export const getMyShop = () => api.get('/shops/my');
export const getShop = (shopId) => api.get(`/shops/${shopId}`);
export const updateShop = (shopId, data) => api.put(`/shops/${shopId}`, data);
export const deleteShop = (shopId) => api.delete(`/shops/${shopId}`);
export const getShopListings = (shopId, params) => api.get(`/shops/${shopId}/listings`, { params });

// Vatican News
export const getVaticanPosts = (params) => api.get('/vatican/posts', { params });
export const triggerVaticanFetch = () => api.post('/vatican/fetch');
export const pinVaticanPost = (postId) => api.post(`/vatican/pin/${postId}`);
export const getParishVaticanSettings = () => api.get('/vatican/parish-settings');
export const updateParishVaticanSettings = (enabled) => api.put('/vatican/parish-settings', null, { params: { enabled } });

// MassTimes.org Import
export const searchMasstimes = (lat, long, page = 1, fetchAll = false) => api.get('/masstimes/search', { params: { lat, long, page, fetch_all: fetchAll }, timeout: 120000 });
export const importMasstimesParishes = (churches) => api.post('/masstimes/import', churches);
export const getUnclaimedParishes = (params) => api.get('/masstimes/unclaimed', { params });
export const claimParish = (parishId, message, roleAtParish) => api.post('/masstimes/claim', { parish_id: parishId, message, role_at_parish: roleAtParish });
export const getParishClaims = (status = 'pending') => api.get('/masstimes/claims', { params: { status } });
export const approveParishClaim = (claimId) => api.post(`/masstimes/claims/${claimId}/approve`);
export const rejectParishClaim = (claimId, reason = '') => api.post(`/masstimes/claims/${claimId}/reject`, null, { params: { reason } });
export const syncParishFromMasstimes = (parishId) => api.post(`/masstimes/sync/${parishId}`);
export const diagnoseImportedParishes = () => api.get('/masstimes/diagnose');
export const repairImportedParishes = () => api.post('/masstimes/repair');
export const resyncAllParishes = () => api.post('/masstimes/resync-all', null, { timeout: 300000 });

// Saint of the Day
export const getSaintToday = () => api.get('/saint-of-the-day/today');
export const getSaintByDate = (dateStr) => api.get(`/saint-of-the-day/date/${dateStr}`);
export const getSaintHistory = (limit = 30) => api.get('/saint-of-the-day/history', { params: { limit } });

// Daily Mass Readings
export const getReadingsByDate = (dateStr, rite = 'novus_ordo') => api.get(`/daily-readings/date/${dateStr}`, { params: { rite } });
export const getReadingsToday = (rite = 'novus_ordo') => api.get('/daily-readings/today', { params: { rite } });

// Habits (Rule of Life)
export const getHabits = () => api.get('/habits');
export const createHabit = (data) => api.post('/habits', data);
export const updateHabit = (habitId, data) => api.put(`/habits/${habitId}`, data);
export const deleteHabit = (habitId) => api.delete(`/habits/${habitId}`);
export const getHabitsToday = (date) => api.get('/habits/today', { params: date ? { date } : {} });
export const logHabit = (habitId, data = {}) => api.post(`/habits/${habitId}/log`, data);
export const getHabitStats = (days = 90) => api.get('/habits/stats', { params: { days } });
export const getHabitTemplates = () => api.get('/habits/templates');
export const applyHabitTemplate = (templateId) => api.post(`/habits/templates/${templateId}/apply`);
export const resetHabitProgress = () => api.post('/habits/reset-progress');
export const getHabitReminderSettings = () => api.get('/habits/reminder-settings');
export const updateHabitReminderSettings = (data) => api.put('/habits/reminder-settings', data);
export const getMonthlyStats = (months = 12) => api.get('/habits/stats/monthly', { params: { months } });
export const reorderHabits = (habits) => api.post('/habits/reorder', { habits });
export const getJournalEntries = (days = 30, habitId) => api.get('/habits/journal', { params: { days, ...(habitId ? { habit_id: habitId } : {}) } });
export const updateLogNotes = (habitId, notes, date) => api.put(`/habits/${habitId}/notes`, { notes }, { params: date ? { date } : {} });
export const exportHabitsCsv = (days = 90) => api.get('/habits/export', { params: { days }, responseType: 'blob' });

// Habits Social (Phase 2)
export const getLiturgicalSuggestions = () => api.get('/habits/social/liturgical-suggestions');
export const dismissLiturgicalSuggestion = (suggestionId) => api.post('/habits/social/liturgical-suggestions/dismiss', { suggestion_id: suggestionId });
export const addSuggestedHabits = (suggestionId) => api.post('/habits/social/liturgical-suggestions/add-habits', { suggestion_id: suggestionId });
export const getChallenges = (scope = 'my', params = {}) => api.get('/habits/social/challenges', { params: { scope, ...params } });
export const getChallengeDetail = (id) => api.get(`/habits/social/challenges/${id}`);
export const createChallenge = (data) => api.post('/habits/social/challenges', data);
export const joinChallenge = (id) => api.post(`/habits/social/challenges/${id}/join`);
export const leaveChallenge = (id) => api.post(`/habits/social/challenges/${id}/leave`);
export const deleteChallenge = (id) => api.delete(`/habits/social/challenges/${id}`);
export const inviteToChallenge = (id, userIds) => api.post(`/habits/social/challenges/${id}/invite`, { user_ids: userIds });
export const inviteAllToChallenge = (id) => api.post(`/habits/social/challenges/${id}/invite-all`);
export const searchChallengeMembers = (params) => api.get('/habits/social/challenges/search-members', { params });
export const getAccountabilityPartners = () => api.get('/habits/social/accountability/partners');
export const sendAccountabilityRequest = (partnerId) => api.post('/habits/social/accountability/request', { partner_user_id: partnerId });
export const acceptAccountability = (pairId) => api.post(`/habits/social/accountability/${pairId}/accept`);
export const declineAccountability = (pairId) => api.post(`/habits/social/accountability/${pairId}/decline`);
export const removeAccountability = (pairId) => api.delete(`/habits/social/accountability/${pairId}`);

// Job Board
export const getJobTypes = () => api.get('/jobs/types');
export const getJobCategories = () => api.get('/jobs/categories');
export const getJobs = (params) => api.get('/jobs', { params });
export const getJob = (jobId) => api.get(`/jobs/${jobId}`);
export const getMyJobs = () => api.get('/jobs/my-jobs');
export const createJob = (data) => api.post('/jobs', data);
export const updateJob = (jobId, data) => api.put(`/jobs/${jobId}`, data);
export const deleteJob = (jobId) => api.delete(`/jobs/${jobId}`);
export const applyForJob = (jobId) => api.post(`/jobs/${jobId}/apply`);
export const getJobAlerts = () => api.get('/jobs/alerts');
export const createJobAlert = (data) => api.post('/jobs/alerts', data);
export const deleteJobAlert = (alertId) => api.delete(`/jobs/alerts/${alertId}`);
export const getJobMapListings = (params) => api.get('/jobs/map/listings', { params });

// Liturgical Calendar
export const getLiturgicalCalendar = (year, month) => api.get('/liturgical-calendar', { params: { year, month } });
export const getLiturgicalCalendarYears = () => api.get('/liturgical-calendar/years');
export const uploadLiturgicalCalendar = (data) => api.post('/liturgical-calendar/upload', data);

export default api;
